// This file contains logic around fetching the unsampled last rows of run histories.
// We need to know the max step (final step with media data) to configure the step slider maximum.
// If all we have is sampled data, we have no way of knowing what the actual max step is (it may not have been sampled).
// So we refetch the last rows while avoiding any sampling (by requesting a relatively small range of steps).

import {produce} from 'immer';
import _ from 'lodash';
import {useEffect, useRef, useState} from 'react';

import {type Data, type QueryConfig} from '../../../state/runs/types';
import {startFunctionPerformanceMeasure} from '../../../util/datadog/workspace/functionPerformance';
import {fetchUnsampledRunHistory} from './history';

type UseAugmentedDataParams = {
  data: Data;
  query: QueryConfig;
};

export function useAugmentedData({data, query}: UseAugmentedDataParams): Data {
  const [augmentedData, setAugmentedData] = useState<Data>(data);
  const [prevData, setPrevData] = useState<Data>(data);
  const lastRequestIDRef = useRef<number | null>(null);

  // Synchronously update augmented data during render when new data arrives.
  // This eliminates the one-render delay that useEffect + setState causes,
  // keeping augmentedData in sync with the incoming data prop.
  if (data !== prevData) {
    if (!data.loading) {
      setPrevData(data);
      setAugmentedData(data);
    }
  }

  // Async augmentation: fetch unsampled last steps for runs with >10k history rows.
  useEffect(() => {
    (async () => {
      const requestID = Math.random();
      lastRequestIDRef.current = requestID;

      const {histories, filteredRunsById} = data;
      const promises: Array<Promise<void>> = [];
      let aug = data;

      for (let i = 0; i < histories.data.length; i++) {
        const r = histories.data[i];
        const iConst = i;

        const maxSampledStep: number | null =
          _.maxBy(r.history, h => h._step)?._step ?? null;
        const sampleSize = query.historySpecs?.[0]?.samples;
        if (
          maxSampledStep == null ||
          sampleSize == null ||
          maxSampledStep < sampleSize
        ) {
          // We don't have enough steps to even trigger sampling. Ignore this entry.
          continue;
        }

        const historyKeys = query.historySpecs?.[0]?.keys;
        if (historyKeys == null) {
          continue;
        }

        const run = filteredRunsById[r.name];
        const runsetID = run.runsetInfo.id;
        const runsetQuery = _.find(query.runsetQueries, q => q.id === runsetID);
        if (runsetQuery == null) {
          throw new Error(
            'Inconsistent data from the server, run does not have a matching runset'
          );
        }
        const {entityName, projectName} = runsetQuery;

        // Start an async process to fetch the unsampled last steps, then
        // immutably update history data with the fetched steps.
        promises.push(
          // eslint-disable-next-line no-loop-func
          (async () => {
            const unsampledHistory = await fetchUnsampledRunHistory({
              runSignature: {entityName, projectName, runName: r.name},
              historyKeys,
              minStep: maxSampledStep + 1,
            });
            if (unsampledHistory == null) {
              return;
            }
            // Immutably append fetched steps.
            // Mutating would break memoization around history data.
            const endMeasure = startFunctionPerformanceMeasure(
              'immerAugmentMediaHistory'
            );
            const nextAug = produce(aug, draft => {
              draft.histories.data[iConst].history.push(...unsampledHistory);
            });
            endMeasure({
              appendedRowCount: unsampledHistory.length,
              historyCount: aug.histories.data.length,
            });
            aug = nextAug;
          })()
        );
      }

      // No augmentations in flight. Finish.
      if (promises.length === 0) {
        return;
      }

      // Wait until all the fetching and appending is done,
      // then set the immutably-updated reference as the source of truth.
      await Promise.all(promises);
      if (requestID === lastRequestIDRef.current) {
        setAugmentedData(aug);
      }
    })();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return augmentedData;
}
